import {
  Box,
  BoxProps,
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  ComboboxRenderPillInput,
  MultiSelect as MantineMultiSelect,
  MultiSelectProps as MantineMultiSelectProps,
  Pill,
} from '@mantine/core';
import { IconCheck, IconChevronDown, IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { ReadOnlyLock } from '../ReadOnlyLock';
import styles from './MultiSelect.module.css';

export type MultiSelectSize = 'md' | 'lg';

type MultiSelectRenderProps = {
  value: string[];
  onChange: (val: string[]) => void;
  onClear: (event: React.MouseEvent) => void;
  hiddenCount: number;
  hasValue: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  error?: ReactNode;
  data?: MantineMultiSelectProps['data'];
  onClick: () => void;
};

export interface MultiSelectProps extends Omit<
  MantineMultiSelectProps,
  | 'variant'
  | 'color'
  | 'styles'
  | 'classNames'
  | 'style'
  | 'size'
  | 'nothingFoundMessage'
  | 'renderPill'
  | 'readOnly'
> {
  /** Input size: 'md' (40px) or 'lg' (48px). Default: 'md' */
  size?: MultiSelectSize;
  /** Add 'fs-mask' class for FullStory masking of sensitive data */
  isMasked?: boolean;
  isReadOnly?: boolean;
  /** Readonly visual style. 'label' (default) hides border/section and renders pills only; 'lock' keeps border and shows a lock icon in place of the chevron. Only applies when `isReadOnly` is true. */
  readOnlyVariant?: 'label' | 'lock';
  render?: (props: MultiSelectRenderProps) => ReactNode;
  renderRootProps?: BoxProps;
}

const findFirstOverflowIndex = (
  pills: HTMLElement[],
  limitRight: number
): number =>
  pills.findIndex(
    (pill) => pill.getBoundingClientRect().right > limitRight + 1
  );

// The badge is only in the DOM once hiddenCount > 0, so the first pass reserves
// no room for it; the layout effect re-runs with the measured badge width.
const getBadgeReservation = (pillsList: HTMLElement): number => {
  const badge = pillsList.querySelector(
    `.${styles.overflowBadge}`
  ) as HTMLElement | null;
  if (!badge) return 0;
  const gap = parseFloat(getComputedStyle(pillsList).columnGap) || 0;
  return badge.offsetWidth + gap;
};

/**
 * Measures layout properties, updates input spacing, and hides overflowing pills.
 * Extracted globally to keep the component hook complexity well below limits.
 */
const calculateHiddenCount = (
  pillsList: HTMLElement | null,
  inputEl: HTMLElement | null,
  sectionEl: HTMLElement | null
): number => {
  if (!pillsList || !inputEl) return 0;

  if (sectionEl) {
    inputEl.style.paddingRight = `${sectionEl.offsetWidth}px`;
  }

  const pills = Array.from(
    pillsList.querySelectorAll(`.${styles.pill}`)
  ) as HTMLElement[];

  if (pills.length === 0) return 0;

  // Reset all pills to visible so we can measure accurately.
  for (const pill of pills) {
    pill.style.display = '';
  }

  const listRight = pillsList.getBoundingClientRect().right;
  if (findFirstOverflowIndex(pills, listRight) === -1) return 0;

  const firstHidden = findFirstOverflowIndex(
    pills,
    listRight - getBadgeReservation(pillsList)
  );
  const hiddenPills = pills.slice(firstHidden);
  for (const pill of hiddenPills) {
    pill.style.display = 'none';
  }

  return hiddenPills.length;
};

const renderErrorContent = (
  error: React.ReactNode,
  isReadOnly?: boolean
): React.ReactNode | undefined => {
  if (isReadOnly) return undefined;
  return error ?? undefined;
};

const defaultRenderOption = ({
  option,
  checked,
}: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
  <div className={styles.optionContent}>
    <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)}>
      {checked && <IconCheck size={12} stroke={2.5} />}
    </span>
    <span>{option.label}</span>
  </div>
);

export const MultiSelect = forwardRef<HTMLInputElement, MultiSelectProps>(
  (
    {
      size = 'md',
      label,
      error,
      disabled,
      isReadOnly,
      placeholder,
      isMasked = false,
      clearable = true,
      searchable = true,
      readOnlyVariant = 'label',
      renderOption: renderOptionProp,
      value,
      defaultValue,
      onChange,
      comboboxProps,
      render,
      renderRootProps,
      ...props
    },
    ref
  ) => {
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(
      defaultValue ?? []
    );
    const currentValue = isControlled ? value : uncontrolledValue;

    const inputRef = useRef<HTMLInputElement>(null);

    const handleChange = (val: string[]) => {
      if (!isControlled) {
        setUncontrolledValue(val);
      }
      onChange?.(val);
    };

    // --- Dynamic pill overflow: hide pills that don't fit, show "+N" badge ---
    const containerRef = useRef<HTMLDivElement>(null!);
    const [hiddenCount, setHiddenCount] = useState(0);

    // Helper: Extract DOM querying to keep the main logic clear
    const getLayoutElements = (container: HTMLDivElement) => {
      return {
        pillsList: container.querySelector(
          `.${styles.pillsList}`
        ) as HTMLElement | null,
        inputEl: container.querySelector(
          `.${styles.input}`
        ) as HTMLElement | null,
        sectionEl: container.querySelector(
          `.${styles.section}[data-position="right"]`
        ) as HTMLElement | null,
      };
    };

    const computeHiddenPills = useCallback(() => {
      const container = containerRef.current;
      if (!container) return;

      const { pillsList, inputEl, sectionEl } = getLayoutElements(container);
      const hidden = calculateHiddenCount(pillsList, inputEl, sectionEl);

      setHiddenCount(hidden);
    }, []);

    // Recompute after render when values change (useLayoutEffect runs before paint).
    useLayoutEffect(() => {
      computeHiddenPills();
    }, [currentValue, hiddenCount, computeHiddenPills]);

    // Recompute on container resize
    useEffect(() => {
      const container = containerRef.current;

      if (container) {
        const observer = new ResizeObserver(computeHiddenPills);
        observer.observe(container);

        return () => {
          observer.disconnect();
        };
      }
      return undefined;
    }, [computeHiddenPills]);

    const renderOption = renderOptionProp ?? defaultRenderOption;

    const showClearButton =
      clearable && currentValue.length > 0 && !disabled && !isReadOnly;

    const handleClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      handleChange([]);
    };

    const isLockVariant = !!isReadOnly && readOnlyVariant === 'lock';

    const lastValue = currentValue[currentValue.length - 1];

    const renderPill = ({
      option,
      value: pillValue,
      onRemove,
      reorderProps,
    }: ComboboxRenderPillInput) => (
      <>
        <Pill
          unstyled
          className={styles.pill}
          withRemoveButton={!isReadOnly && !option?.disabled}
          onRemove={onRemove}
          disabled={disabled}
          {...reorderProps}
        >
          {option?.label ?? pillValue}
        </Pill>
        {pillValue === lastValue && hiddenCount > 0 && (
          <span className={styles.overflowBadge}>+{hiddenCount}</span>
        )}
      </>
    );

    // Right section: [clear X] [chevron or lock]
    const rightSection = (
      <span className={styles.rightControls}>
        {showClearButton && (
          <button
            type="button"
            aria-label="Clear all values"
            className={styles.clearButton}
            onClick={handleClear}
            tabIndex={-1}
          >
            <IconX size={12} stroke={2} />
          </button>
        )}
        {isLockVariant ? (
          <ReadOnlyLock />
        ) : (
          <IconChevronDown size={16} stroke={2} />
        )}
      </span>
    );

    const mantineMultiSelect = (
      <MantineMultiSelect
        ref={ref || inputRef}
        unstyled
        classNames={{
          root: styles.root,
          wrapper: clsx(styles.wrapper, render && styles.hiddenWrapper),
          input: clsx(styles.input, isMasked && 'fs-mask'),
          section: styles.section,
          label: styles.label,
          error: styles.error,
          required: styles.required,
          pill: styles.pill,
          pillsList: styles.pillsList,
          inputField: styles.inputField,
        }}
        comboboxProps={{
          ...comboboxProps,
          classNames: {
            dropdown: clsx(styles.dropdown, size === 'lg' && styles.dropdownLg),
            options: styles.options,
            option: styles.option,
          },
        }}
        wrapperProps={{
          'data-input-size': size,
          ...(isReadOnly && { 'data-readonly': true }),
          ...(isLockVariant && { 'data-readonly-variant': 'lock' }),
        }}
        rightSection={rightSection}
        rightSectionPointerEvents="auto"
        withCheckIcon={false}
        value={currentValue}
        onChange={handleChange}
        clearable={false}
        searchable={searchable}
        label={label}
        error={renderErrorContent(
          error as unknown as React.ReactNode,
          isReadOnly
        )}
        disabled={disabled}
        readOnly={isReadOnly}
        placeholder={isReadOnly && !isLockVariant ? '-' : placeholder}
        renderOption={renderOption}
        renderPill={renderPill}
        {...props}
      />
    );

    // If custom render is provided, hide the default input and provide onClick handler
    if (render) {
      const handleClick = () => {
        if (disabled || isReadOnly) return;
        const inputField = containerRef.current?.querySelector(
          `.${styles.inputField}`
        ) as HTMLInputElement;
        inputField?.focus();
      };

      return (
        <Box
          {...renderRootProps}
          ref={containerRef}
          style={{ position: 'relative', ...renderRootProps?.style }}
        >
          {render({
            value: currentValue,
            onChange: handleChange,
            onClear: handleClear,
            hiddenCount,
            hasValue: currentValue.length > 0,
            isDisabled: disabled,
            isReadOnly,
            error: renderErrorContent(
              error as unknown as React.ReactNode,
              isReadOnly
            ),
            data: props.data,
            onClick: handleClick,
          })}
          {mantineMultiSelect}
        </Box>
      );
    }

    return <div ref={containerRef}>{mantineMultiSelect}</div>;
  }
);

MultiSelect.displayName = 'MultiSelect';
